import { ChangeEvent, FC, useEffect, useRef, useState } from "react";
import { Box, InputAdornment, TextField, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { CountryCode, parsePhoneNumber } from "libphonenumber-js";

import { useCountries } from "services/app";

const formatPhoneForDisplay = (
  phoneValue: string,
  countryCode: CountryCode,
): string => {
  try {
    const phoneNumber = parsePhoneNumber(phoneValue, countryCode);
    if (phoneNumber) {
      return phoneNumber.formatNational().replace(/-/g, " ");
    }
  } catch (error) {
    // Ignore parsing errors and fall back to raw value
  }
  return phoneValue;
};

const useStyles = makeStyles(theme => ({
  textInputRoot: {
    textAlign: "left",
    "& label.Mui-focused": {
      color: theme.custom.palette.purple.main,
    },
    "&:hover label": {
      color: theme.custom.palette.purple.main,
    },
    "& .MuiOutlinedInput-root": {
      maxHeight: "56px",
      display: "flex",
      alignItems: "center",
      position: "relative",
      "&:hover fieldset": {
        borderColor: theme.custom.palette.purple.main,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.custom.palette.purple.main,
      },
      "&.Mui-error $phoneAdornmentWrapper::after": {
        backgroundColor: theme.palette.error.main,
      },
      "&:hover $phoneAdornmentWrapper::after": {
        backgroundColor: theme.custom.palette.purple.main,
      },
      "&.Mui-error:hover $phoneAdornmentWrapper::after": {
        backgroundColor: theme.custom.palette.purple.main,
      },
      "& .MuiInputAdornment-root": {
        height: "56px !important",
        minHeight: "56px !important",
        maxHeight: "56px !important",
        alignSelf: "stretch",
      },
      "& .MuiInputAdornment-positionStart": {
        margin: 0,
        height: "56px",
      },
    },
  },
  phoneAdornment: {
    color: theme.palette.text.secondary,
    fontSize: "inherit",
    paddingRight: theme.spacing(2),
  },
  phoneAdornmentWrapper: {
    display: "flex",
    alignItems: "center",
    paddingRight: theme.spacing(1),
    marginRight: theme.spacing(1),
    position: "relative",
    "&::after": {
      content: '""',
      position: "absolute",
      right: theme.spacing(1),
      top: 0,
      bottom: 0,
      width: "1px",
      backgroundColor: theme.palette.divider,
      transition: "backgroundColor 0.2s",
    },
  },
}));

interface PhoneInputProps {
  value: string;
  countryCode: CountryCode;
  onChange: (digitsOnly: string) => void;
  onBlur: () => void;
  error: boolean;
  helperText: string;
  disabled: boolean;
}

const PhoneInput: FC<PhoneInputProps> = ({
  value,
  countryCode,
  onChange,
  onBlur,
  error,
  helperText,
  disabled,
}) => {
  const classes = useStyles();
  const { data: countries = [] } = useCountries();
  const [displayPhone, setDisplayPhone] = useState("");

  const phoneCode =
    countries.find(c => c.code === countryCode)?.phone_code || "";
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!value) {
      setDisplayPhone("");
      return;
    }
    setDisplayPhone(formatPhoneForDisplay(value, countryCode));
  }, [value, countryCode]);

  const handlePhoneChange = (event: ChangeEvent<HTMLInputElement>) => {
    const input = event.target.value;
    let digitsOnly = input.replace(/\D/g, "");

    if (digitsOnly === value) {
      return;
    }

    if (digitsOnly.length === 0) {
      setDisplayPhone("");
      onChange("");
      return;
    }

    // Strip leading country code if user types it
    if (phoneCode && digitsOnly.startsWith(phoneCode)) {
      digitsOnly = digitsOnly.slice(phoneCode.length);
    }

    setDisplayPhone(formatPhoneForDisplay(digitsOnly, countryCode));
    onChange(digitsOnly);
  };

  return (
    <TextField
      variant="outlined"
      id="phone"
      type="tel"
      name="phone"
      label="Phone"
      inputRef={inputRef}
      fullWidth
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <Box className={classes.phoneAdornmentWrapper}>
              <Typography className={classes.phoneAdornment}>
                +{phoneCode}
              </Typography>
            </Box>
          </InputAdornment>
        ),
      }}
      onChange={handlePhoneChange}
      onBlur={onBlur}
      value={displayPhone}
      error={error}
      helperText={helperText}
      disabled={disabled}
      className={classes.textInputRoot}
    />
  );
};

export default PhoneInput;
